import * as db from './db';

const VOTES_KEY = 'betbuds_votes';

function getStored() {
  const stored = localStorage.getItem(VOTES_KEY);
  if (stored) return JSON.parse(stored);
  localStorage.setItem(VOTES_KEY, JSON.stringify([]));
  return [];
}

function save(data) {
  localStorage.setItem(VOTES_KEY, JSON.stringify(data));
}

function dbWrite(promise) {
  promise.catch((err) => console.warn('[vote] DynamoDB write failed:', err));
}

export const voteService = {
  getForActivity(activityId) {
    return getStored().find((v) => v.activityId === activityId) || null;
  },

  getForMasterEvent(masterEventId) {
    return getStored().filter((v) => v.masterEventId === masterEventId);
  },

  create({ activityId, masterEventId, question, options, createdBy, allowMultiple, anonymous }) {
    const votes = getStored();
    const vote = {
      id: `vote_${Date.now()}`,
      activityId: activityId || null,
      masterEventId,
      question,
      options: options.map((o, i) => ({ id: `opt_${i}`, text: o, votes: [] })),
      createdBy,
      allowMultiple: allowMultiple || false,
      anonymous: anonymous || false,
      status: 'open',
      createdAt: new Date().toISOString(),
    };
    votes.push(vote);
    save(votes);

    // DynamoDB dual-write
    dbWrite(db.createVote({
      voteId: vote.id,
      activityId: vote.activityId,
      masterEventId: vote.masterEventId,
      question: vote.question,
      options: JSON.stringify(vote.options),
      createdBy: vote.createdBy,
      allowMultiple: vote.allowMultiple,
      anonymous: vote.anonymous,
      status: vote.status,
    }));

    return vote;
  },

  castVote(voteId, optionId, userId) {
    const votes = getStored();
    const poll = votes.find((v) => v.id === voteId);
    if (!poll) throw new Error('Poll not found');
    if (poll.status !== 'open') throw new Error('Poll is closed');

    if (!poll.allowMultiple) {
      poll.options.forEach((o) => { o.votes = o.votes.filter((uid) => uid !== userId); });
    }

    const opt = poll.options.find((o) => o.id === optionId);
    if (!opt) throw new Error('Option not found');

    if (opt.votes.includes(userId)) {
      opt.votes = opt.votes.filter((uid) => uid !== userId);
    } else {
      opt.votes.push(userId);
    }

    save(votes);

    // DynamoDB dual-write
    dbWrite(db.updateVote(voteId, { options: JSON.stringify(poll.options) }));

    return poll;
  },

  closePoll(voteId) {
    const votes = getStored();
    const poll = votes.find((v) => v.id === voteId);
    if (!poll) throw new Error('Poll not found');
    poll.status = 'closed';
    poll.closedAt = new Date().toISOString();
    save(votes);

    // DynamoDB dual-write
    dbWrite(db.updateVote(voteId, { status: 'closed', closedAt: poll.closedAt }));

    return poll;
  },

  reopenPoll(voteId) {
    const votes = getStored();
    const poll = votes.find((v) => v.id === voteId);
    if (!poll) throw new Error('Poll not found');
    poll.status = 'open';
    save(votes);

    // DynamoDB dual-write
    dbWrite(db.updateVote(voteId, { status: 'open' }));

    return poll;
  },

  deletePoll(voteId) {
    save(getStored().filter((v) => v.id !== voteId));

    // DynamoDB dual-write
    dbWrite(db.deleteVote(voteId));
  },

  getResults(voteId) {
    const poll = getStored().find((v) => v.id === voteId);
    if (!poll) return null;
    const totalVotes = poll.options.reduce((s, o) => s + o.votes.length, 0);
    return {
      ...poll,
      totalVotes,
      options: poll.options.map((o) => ({
        ...o,
        count: o.votes.length,
        percentage: totalVotes > 0 ? Math.round((o.votes.length / totalVotes) * 100) : 0,
      })),
    };
  },

  parseVoteCommand(text) {
    const match = text.match(/^\/vote\s+(.+)/i);
    if (!match) return null;
    const parts = match[1].split('|').map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) return null;
    return {
      question: parts[0],
      options: parts.slice(1),
    };
  },
};
